import has from 'lodash-es/has';
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import lottie from 'lottie-web';
import { ENTERED } from 'react-transition-group/Transition';
import Link from './shared/Link';
import utils from '../../../common/utils';
import fabric from '../../../common/fabric';
import { Matrix } from '../../../common/lib/matrix';

const Lottie = props => {
  let animation = null;
  const container = React.useRef(null);
  let playTimeout = null;

  React.useEffect(() => {
    if (!props.animationData) {
      return;
    }

    const { ludus } = props.params;

    // Configure and load animation
    const animationConfig = {
      container: container.current,
      renderer: 'svg',
      loop: ludus.loop,
      autoplay: false,
      animationData: JSON.parse(props.animationData),
    };

    animation = lottie.loadAnimation(animationConfig);

    // We have to wait for the config to be ready before setting up reverse mode
    animation.setSpeed(ludus.speed);
    if (ludus.reverse === true) {
      animation.goToAndStop(animation.totalFrames - 1, false);
      animation.setDirection(-1);
    }

    // Play if ready
    if (props.transitionStatus === ENTERED) {
      play();
    }
    return () => {
      stop();
      if (animation.isLoaded) {
        animation.destroy();
      } else {
        animation.addEventListener('DOMLoaded', () => {
          animation.destroy();
        });
      }
    };
  }, [props.transitionStatus, props.params, container.current]);

  const play = () => {
    clearTimeout(playTimeout);
    playTimeout = setTimeout(() => {
      animation.play();
    }, props.params.ludus.delay * 1000);
  };

  const stop = () => {
    animation.pause();
    clearTimeout(playTimeout);
    playTimeout = null;
  };

  const {
    style,
    groupOpacity,
    hyperlink,
    params,
    matrixGroup,
    globalScale,
  } = props;

  const groupMatrix = props.matrixGroup
    ? fabric.util.multiplyTransformMatrices(
        matrixGroup,
        params.ludus.transformMatrix,
      )
    : params.ludus.transformMatrix;

  const transformMatrix = fabric.util.multiplyTransformMatrices(
    [globalScale, 0, 0, globalScale, 0, 0],
    groupMatrix,
  );
  const options = fabric.util.qrDecompose(transformMatrix);
  const matrix = new Matrix(transformMatrix);
  matrix.scale(1 / options.scaleX, 1 / options.scaleY);
  const opts = matrix.decompose();
  const width = params.width * options.scaleX;
  const height = params.height * options.scaleY;
  const transform = `translate(-50%, -50%) translate(${options.translateX}px, ${options.translateY}px) rotate(${options.angle}deg)  skew(${opts.skew.x}rad, ${options.skewY}deg)`;

  const blockStyles = {
    ...style,
    top: 0,
    left: 0,
    width,
    height,
    opacity: groupOpacity,
    mixBlendMode:
      params.globalCompositeOperation === 'source-over'
        ? 'normal'
        : params.globalCompositeOperation,
    filter: params.shadow
      ? utils.getDropShadowCSSFilterFromFabricShadow(params.shadow, 0, 1, false)
      : 'none',
    transform,
    transformOrigin: 'center center',
  };

  return (
    <Link hyperlink={hyperlink}>
      <div
        className={
          utils.isChrome && utils.isMobile ? 'lottie lottie-android' : 'lottie'
        }
        ref={container}
        style={blockStyles}
      />
    </Link>
  );
};

Lottie.propTypes = {
  style: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ).isRequired,
  params: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.bool,
      PropTypes.func,
      PropTypes.number,
      PropTypes.object,
      PropTypes.string,
    ]),
  ).isRequired,
  groupOpacity: PropTypes.number.isRequired,
  transitionStatus: PropTypes.string.isRequired,
  hyperlink: PropTypes.string,
  animationData: PropTypes.string,
  matrixGroup: PropTypes.arrayOf(PropTypes.number),
  globalScale: PropTypes.number,
};
Lottie.defaultProps = {
  hyperlink: null,
  animationData: null,
  matrixGroup: null,
  globalScale: 1,
};

export default connect((state, ownProps) => {
  if (
    has(ownProps, 'params.ludus.url') &&
    has(state.preloadReducer.lottie, ownProps.params.ludus.url)
  ) {
    return {
      animationData: state.preloadReducer.lottie[ownProps.params.ludus.url],
    };
  }
  return { animationData: ownProps.params.ludus.data };
})(Lottie);
